import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Building2, Download, Inbox, Lock, LogOut, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_SESSION_KEY = 'portalfinca_admin_auth';
const DEFAULT_ADMIN_USERNAME = process.env.REACT_APP_ADMIN_USERNAME || 'admin';

const toBasicAuth = (username, password) => {
  const bytes = new TextEncoder().encode(`${username}:${password}`);
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join('');
  return `Basic ${btoa(binary)}`;
};

export default function Admin() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [authHeader, setAuthHeader] = useState(() => sessionStorage.getItem(ADMIN_SESSION_KEY) || '');
  const [credentials, setCredentials] = useState({ username: DEFAULT_ADMIN_USERNAME, password: '' });
  const isAuthenticated = Boolean(authHeader);

  const load = useCallback(async () => {
    if (!authHeader) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: { Authorization: authHeader },
      });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 503) {
        sessionStorage.removeItem(ADMIN_SESSION_KEY);
        setAuthHeader('');
        setLeads([]);
        setCredentials((current) => ({ ...current, password: '' }));
        setError('Credenciales incorrectas o acceso no configurado.');
      } else {
        setError('No se pudieron cargar los leads.');
      }
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const login = async (e) => {
    e.preventDefault();
    const token = toBasicAuth(credentials.username, credentials.password);
    setLoading(true);
    setError(null);
    try {
      await axios.get(`${API}/leads/stats`, {
        headers: { Authorization: token },
      });
      sessionStorage.setItem(ADMIN_SESSION_KEY, token);
      setAuthHeader(token);
      setCredentials((current) => ({ ...current, password: '' }));
    } catch (e) {
      setError('Credenciales incorrectas.');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(ADMIN_SESSION_KEY);
    setAuthHeader('');
    setLeads([]);
    setCredentials((current) => ({ ...current, password: '' }));
  };

  const exportCsv = () => {
    if (!leads.length) return;
    const headers = ['Fecha', 'Nombre', 'Email', 'Despacho', 'Comunidades', 'Teléfono', 'Idioma', 'Mensaje'];
    const rows = leads.map((l) => [
      new Date(l.created_at).toLocaleString('es-ES'),
      l.name,
      l.email,
      l.company,
      l.communities,
      l.phone || '',
      l.locale,
      (l.message || '').replace(/\n/g, ' ').replace(/"/g, '""'),
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `portalfinca-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-app" data-testid="admin-page">
      <header className="border-b border-app bg-[#F9F8F6]/90 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="admin-home">
            <span className="w-9 h-9 rounded-md bg-primary-app flex items-center justify-center text-white">
              <Building2 size={18} />
            </span>
            <span className="font-serif text-2xl font-semibold text-app">PortalFinca</span>
            <span className="ml-2 text-xs label-eyebrow">Admin · Leads</span>
          </Link>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={!isAuthenticated}
              className="inline-flex items-center gap-1.5 text-sm text-app-secondary border border-app rounded-md px-3 py-1.5 hover:bg-surface-muted"
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} /> Actualizar
            </button>
            <button
              onClick={exportCsv}
              disabled={!leads.length}
              className="inline-flex items-center gap-1.5 text-sm bg-primary-app text-white rounded-md px-3 py-1.5 hover:bg-[#8F3E2E] disabled:opacity-50"
              data-testid="admin-export"
            >
              <Download size={14} /> Exportar CSV
            </button>
            {isAuthenticated && (
              <button
                onClick={logout}
                className="inline-flex items-center gap-1.5 text-sm text-app-secondary border border-app rounded-md px-3 py-1.5 hover:bg-surface-muted"
                data-testid="admin-logout"
              >
                <LogOut size={14} /> Salir
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-app-secondary mb-6 hover:text-app">
          <ArrowLeft size={14} /> Volver al sitio
        </Link>

        {!isAuthenticated && (
          <div className="max-w-md mx-auto bg-surface border border-app rounded-lg p-8 shadow-sm" data-testid="admin-login">
            <div className="w-12 h-12 rounded-md bg-primary-app/10 text-primary-app flex items-center justify-center mb-5">
              <Lock size={22} />
            </div>
            <h1 className="font-serif text-3xl text-app mb-2">Acceso privado</h1>
            <p className="text-sm text-app-secondary mb-6">
              Este panel es solo para revisar solicitudes de demo recibidas.
            </p>
            <form onSubmit={login} className="space-y-4">
              <label className="block">
                <span className="block text-sm font-medium text-app-secondary mb-1.5">Usuario</span>
                <input
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="admin-input"
                  autoComplete="username"
                  data-testid="admin-username"
                />
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-app-secondary mb-1.5">Contraseña</span>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="admin-input"
                  autoComplete="current-password"
                  data-testid="admin-password"
                />
              </label>
              {error && <div className="text-sm text-[#C53030]">{error}</div>}
              <button
                type="submit"
                disabled={loading || !credentials.username || !credentials.password}
                className="w-full inline-flex items-center justify-center gap-2 bg-primary-app text-white rounded-md px-4 py-2.5 hover:bg-[#8F3E2E] disabled:opacity-50"
                data-testid="admin-login-submit"
              >
                {loading ? 'Comprobando…' : 'Entrar'}
              </button>
            </form>
          </div>
        )}

        {isAuthenticated && (
          <>
        <div className="flex flex-wrap items-end justify-between gap-3 mb-8">
          <div>
            <h1 className="font-serif text-4xl text-app">Solicitudes de demo</h1>
            <p className="text-app-secondary mt-1">
              {leads.length} {leads.length === 1 ? 'lead capturado' : 'leads capturados'}
            </p>
          </div>
        </div>

        {loading && (
          <div className="bg-surface border border-app rounded-lg p-12 text-center text-app-muted">Cargando…</div>
        )}

        {error && !loading && (
          <div className="bg-[#C53030]/10 border border-[#C53030]/30 text-[#C53030] rounded-md p-4">{error}</div>
        )}

        {!loading && !error && leads.length === 0 && (
          <div className="bg-surface border border-app rounded-lg p-12 text-center" data-testid="admin-empty">
            <Inbox size={32} className="mx-auto text-app-muted mb-3" />
            <h3 className="font-serif text-2xl text-app mb-2">Aún no hay solicitudes</h3>
            <p className="text-app-secondary text-sm">
              Cuando alguien rellene el formulario en la landing, aparecerá aquí.
            </p>
          </div>
        )}

        {!loading && !error && leads.length > 0 && (
          <div className="bg-surface border border-app rounded-lg overflow-hidden" data-testid="admin-leads-table">
            <table className="w-full">
              <thead className="bg-surface-muted border-b border-app">
                <tr>
                  <th className="text-left text-xs font-medium text-app-muted uppercase tracking-wider px-4 py-3">Fecha</th>
                  <th className="text-left text-xs font-medium text-app-muted uppercase tracking-wider px-4 py-3">Nombre</th>
                  <th className="text-left text-xs font-medium text-app-muted uppercase tracking-wider px-4 py-3">Email</th>
                  <th className="text-left text-xs font-medium text-app-muted uppercase tracking-wider px-4 py-3 hidden md:table-cell">Despacho</th>
                  <th className="text-left text-xs font-medium text-app-muted uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Comunidades</th>
                  <th className="text-left text-xs font-medium text-app-muted uppercase tracking-wider px-4 py-3 hidden lg:table-cell">Teléfono</th>
                  <th className="text-left text-xs font-medium text-app-muted uppercase tracking-wider px-4 py-3">Idioma</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-b border-app last:border-0 hover:bg-surface-muted" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 text-xs text-app-muted">{new Date(l.created_at).toLocaleString('es-ES')}</td>
                    <td className="px-4 py-3 text-sm font-medium text-app">{l.name}</td>
                    <td className="px-4 py-3 text-sm text-app-secondary">{l.email}</td>
                    <td className="px-4 py-3 text-sm text-app-secondary hidden md:table-cell">{l.company}</td>
                    <td className="px-4 py-3 text-sm text-app-secondary hidden lg:table-cell">{l.communities}</td>
                    <td className="px-4 py-3 text-sm text-app-secondary hidden lg:table-cell">{l.phone || '—'}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs uppercase font-medium bg-surface-muted text-app-secondary px-2 py-0.5 rounded">
                        {l.locale}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
          </>
        )}
      </main>
      <style>{`
        .admin-input {
          width: 100%;
          padding: 0.75rem 1rem;
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 0.375rem;
          color: var(--text-primary);
          font-size: 0.9375rem;
          transition: border-color .15s, box-shadow .15s;
        }
        .admin-input:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(179, 78, 58, 0.12);
        }
      `}</style>
    </div>
  );
}
